/**
 * The month so far, summarised for the Dele Longevity Android app (Super App Phase 2).
 * When Finance Cockpit runs as the app's Money space, it hands this to the shell, which
 * shows one line of it on Today. Pure and JSON-safe; the same maths as the Monday brief
 * (monthSoFar and paceVerdict), so Today, Home and the email agree. Provisional always.
 */
import type { Account, Category, Month, ProvisionalTransaction } from '../data/types';
import type { PayeeRule } from './payee';
import { monthSoFar } from './sofar';

export type Pace = 'on pace' | 'a little ahead of pace' | 'ahead of pace';

/** Share of the ceiling used against share of the month gone. The brief's own thresholds. */
export function paceVerdict(used: number, elapsed: number): Pace {
  return used <= elapsed + 0.05 ? 'on pace' : used <= elapsed + 0.15 ? 'a little ahead of pace' : 'ahead of pace';
}

export type ShellSummary =
  | { v: 1; state: 'no-plan'; asOf: string }
  | { v: 1; state: 'no-alerts'; asOf: string; month: string; ceiling: number | null }
  | {
      v: 1; state: 'so-far'; asOf: string; month: string;
      spent: number; ceiling: number | null; ceilingLeft: number | null; perDayLeft: number | null; daysLeft: number;
      /** Null without a ceiling, or while an account is not covered: pace is then unknown, never guessed. */
      pace: Pace | null;
      notCovered: string[]; lastAlert: string | null; provisional: true;
    };

export function shellSummary(plan: Month | null, rows: ProvisionalTransaction[], categories: Category[], rules: PayeeRule[],
  accounts: Account[], now = new Date()): ShellSummary {
  const asOf = now.toISOString();
  if (!plan) return { v: 1, state: 'no-plan', asOf };
  const ceiling = plan.personal_spend_cap ?? null;
  if (rows.length === 0) return { v: 1, state: 'no-alerts', asOf, month: plan.month_start, ceiling };
  const s = monthSoFar(rows, categories, rules, plan, accounts, now);
  const daysIn = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const elapsed = (now.getDate() - 1) / daysIn;
  const pace = ceiling && s.notCovered.length === 0 ? paceVerdict(s.money.personal / ceiling, elapsed) : null;
  return {
    v: 1, state: 'so-far', asOf, month: plan.month_start,
    spent: s.money.personal, ceiling, ceilingLeft: s.ceilingLeft, perDayLeft: s.perDayLeft, daysLeft: s.daysLeft,
    pace, notCovered: s.notCovered, lastAlert: s.lastSeen, provisional: true,
  };
}
